import React, { useState, useEffect, useRef } from 'react';
import { api } from '../services/api';

/**
 * SystemMetrics - compact inline bar showing host CPU/RAM/HDD + Uptime.
 * Sits at the top of main-content in one horizontal row.
 * Polls /api/system/metrics every 10s.
 */
function SystemMetrics() {
  const [metrics, setMetrics] = useState(null);
  const [error, setError] = useState(false);
  const intervalRef = useRef(null);

  useEffect(() => {
    fetchMetrics();
    intervalRef.current = setInterval(fetchMetrics, 10000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  async function fetchMetrics() {
    try {
      const data = await api.getSystemMetrics();
      setMetrics(data);
      setError(false);
    } catch {
      setError(true);
    }
  }

  if (error) {
    return (
      <div className="system-metrics-bar system-metrics-error">
        <span className="metrics-status-text">System: Keine Verbindung</span>
      </div>
    );
  }

  if (!metrics) {
    return (
      <div className="system-metrics-bar system-metrics-loading">
        <span className="metrics-status-text">System: Lade...</span>
      </div>
    );
  }

  const { host } = metrics;
  const cpuPercent = host.cpu.percent;
  const ramPercent = host.ram.percent;
  const diskPercent = host.disk.percent;

  return (
    <div className="system-metrics-bar">
      {/* CPU */}
      <div className="metrics-item">
        <span className="metrics-item-label">CPU</span>
        <MetricsBar percent={cpuPercent} type="cpu" />
        <span className="metrics-item-value">{cpuPercent}%</span>
      </div>

      <div className="metrics-divider" />

      {/* RAM */}
      <div className="metrics-item">
        <span className="metrics-item-label">RAM</span>
        <MetricsBar percent={ramPercent} type="ram" />
        <span className="metrics-item-value">
          {formatBytes(host.ram.used)} / {formatBytes(host.ram.total)}
        </span>
      </div>

      <div className="metrics-divider" />

      {/* HDD */}
      <div className="metrics-item">
        <span className="metrics-item-label">HDD</span>
        <MetricsBar percent={diskPercent} type="hdd" />
        <span className="metrics-item-value">
          {formatBytes(host.disk.used)} / {formatBytes(host.disk.total)}
        </span>
      </div>

      <div className="metrics-divider" />

      {/* Uptime */}
      <div className="metrics-item metrics-item-uptime">
        <span className="metrics-item-label">Uptime</span>
        <span className="metrics-item-value">{formatUptime(host.uptime)}</span>
      </div>
    </div>
  );
}

/**
 * Compact MC-style bar for inline display.
 */
function MetricsBar({ percent, type }) {
  const clamped = Math.max(0, Math.min(100, percent || 0));
  const isWarning = clamped >= 80;
  const isCritical = clamped >= 95;

  return (
    <div className={`mc-bar-track mc-bar-${type}`}>
      <div
        className={`mc-bar-fill ${isCritical ? "mc-bar-critical" : isWarning ? "mc-bar-warning" : ""}`}
        style={{ width: `${clamped}%` }}
      />
      <div className="mc-bar-segments" />
    </div>
  );
}

function formatBytes(bytes) {
  if (!bytes || bytes === 0) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let i = 0;
  let val = bytes;
  while (val >= 1024 && i < units.length - 1) {
    val /= 1024;
    i++;
  }
  return `${val.toFixed(i > 1 ? 1 : 0)} ${units[i]}`;
}

function formatUptime(seconds) {
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (d > 0) return `${d}d ${h}h ${m}m`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

export default SystemMetrics;
